import type { Element } from "../model/board";

// Quiet filtered air and short acoustic-style tones, without arcade waveforms.
class GameAudio {
  private context?: AudioContext;
  private output?: GainNode;
  private noise?: AudioBuffer;
  private last = new Map<string, number>();
  enabled = true;
  constructor() {
    try { this.enabled = localStorage.getItem("mergeborn-sound") !== "off"; } catch { /* session preference */ }
  }
  unlock(): void {
    if (!this.enabled) return;
    if (!this.context) {
      const Audio = window.AudioContext;
      if (!Audio) return;
      const ctx = this.context = new Audio();
      const compressor = ctx.createDynamicsCompressor();
      compressor.threshold.value = -20; compressor.ratio.value = 4;
      this.output = ctx.createGain(); this.output.gain.value = 0.38;
      this.output.connect(compressor); compressor.connect(ctx.destination);
      this.noise = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
      const data = this.noise.getChannelData(0);
      for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
      // A little room tail rounds the crystal and wooden tones.
      const room = ctx.createConvolver(), wet = ctx.createGain(); wet.gain.value = 0.12;
      room.buffer = ctx.createBuffer(2, Math.floor(ctx.sampleRate * 0.75), ctx.sampleRate);
      for (let channel = 0; channel < 2; channel++) {
        const tail = room.buffer.getChannelData(channel);
        for (let i = 0; i < tail.length; i++) tail[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / tail.length, 3) * 0.24;
      }
      this.output.connect(room); room.connect(wet); wet.connect(compressor);
    }
    if (this.context.state === "suspended") void this.context.resume().catch(() => {});
  }
  toggle(): void {
    this.enabled = !this.enabled;
    if (this.output) this.output.gain.value = this.enabled ? 0.38 : 0;
    try { localStorage.setItem("mergeborn-sound", this.enabled ? "on" : "off"); } catch { /* session preference */ }
    if (this.enabled) this.unlock();
  }
  private ready(key: string, spacing = 70): boolean {
    if (!this.enabled || !this.context || this.context.state !== "running") return false;
    const now = this.context.currentTime * 1000;
    if (now - (this.last.get(key) ?? -Infinity) < spacing) return false;
    this.last.set(key, now); return true;
  }
  private air(duration: number, frequency: number, endFrequency: number, volume: number, delay = 0): void {
    const ctx = this.context!, at = ctx.currentTime + delay;
    const source = ctx.createBufferSource(), filter = ctx.createBiquadFilter(), envelope = ctx.createGain();
    source.buffer = this.noise!; filter.type = "bandpass"; filter.Q.value = 0.65;
    filter.frequency.setValueAtTime(frequency, at);
    filter.frequency.exponentialRampToValueAtTime(endFrequency, at + duration);
    envelope.gain.setValueAtTime(0, at);
    envelope.gain.linearRampToValueAtTime(volume, at + Math.min(0.025, duration / 4));
    envelope.gain.exponentialRampToValueAtTime(0.0001, at + duration);
    source.connect(filter); filter.connect(envelope); envelope.connect(this.output!);
    source.start(at, Math.random() * 0.5); source.stop(at + duration);
    source.onended = () => { source.disconnect(); filter.disconnect(); envelope.disconnect(); };
  }
  private chime(frequency: number, volume: number, duration: number, delay = 0): void {
    const ctx = this.context!, at = ctx.currentTime + delay;
    [1, 2.01, 3.97].forEach((ratio, i) => {
      const tone = ctx.createOscillator(), envelope = ctx.createGain(); tone.type = "sine";
      tone.frequency.value = frequency * ratio;
      const sustain = duration / (1 + i * 1.3);
      envelope.gain.setValueAtTime(0, at);
      envelope.gain.linearRampToValueAtTime(volume / (1 + i * 5), at + 0.009);
      envelope.gain.exponentialRampToValueAtTime(0.0001, at + sustain);
      tone.connect(envelope); envelope.connect(this.output!);
      tone.start(at); tone.stop(at + sustain);
      tone.onended = () => { tone.disconnect(); envelope.disconnect(); };
    });
  }
  selection(): void { if (this.ready("select")) { this.air(0.07, 450, 250, 0.035); this.chime(392, 0.035, 0.12); } }
  attack(element: Element, tier: number): void {
    if (!this.ready(element, 95)) return;
    const band = tier < 3 ? 0 : tier < 5 ? 1 : tier < 6 ? 2 : 3;
    if (element === "fire") this.air(0.14 + band * 0.055, 350, 1500 + band * 350, 0.15 + band * 0.03);
    else if (element === "ice") {
      this.air(0.12, 2400, 1300, 0.05);
      this.chime(880 - band * 110, 0.055, 0.32 + band * 0.06);
      if (band) this.chime(1320, 0.025, 0.21, 0.045);
    } else {
      this.air(0.1 + band * 0.035, 2600, 650, 0.17);
      this.chime(130 - band * 15, 0.07, 0.15);
      if (band >= 2) this.air(0.1, 1700, 450, 0.065, 0.08);
    }
    if (band === 3) this.chime(98, 0.07, 0.32);
  }
  merge(tier: number): void {
    if (!this.ready("merge")) return;
    this.air(0.1, 400, 180, 0.1);
    this.chime(392, 0.08, 0.25);
    this.chime(587, 0.065, 0.4, 0.08);
    if (tier >= 3) this.chime(784, 0.055, 0.6, 0.16);
  }
  reward(): void { if (this.ready("reward")) { this.chime(659, 0.055, 0.4); this.chime(880, 0.055, 0.55, 0.09); } }
  result(won: boolean, breakthrough = false): void {
    if (!this.ready("result", 1000)) return;
    const notes = won ? [523, 659, 784, 1046] : [440, 349];
    notes.forEach((note, i) => this.chime(note, 0.07, 0.8, i * 0.11));
    if (breakthrough) [784, 1046, 1318].forEach((note, i) => this.chime(note, 0.06, 1.1, 0.65 + i * 0.16));
  }
}
export const gameAudio = new GameAudio();
